import React from "react";
import format from "string-format";
import NotificationStore from "../../Store/notification-store";

export interface NotificationRow {
  textKey: string;
  outcome?: string;
  values: { [key: string]: string };
  // Whether this line has been dealt with, and whether it is a line that asks for anything at all.
  // requiresAction is already resolved by the API — a row whose producer said nothing has been
  // folded into the notification's own type there — so there is no third state to handle (IP-512).
  isCompleted?: boolean;
  requiresAction?: boolean;
  // Name of the customer the row belongs to, absent when it belongs to none. The grouping key.
  scopeName?: string;
  // Where the row sits in the stored notification, which is how a single row is ticked. The API
  // re-checks that this reader may touch it, so holding it grants nothing.
  position?: number;
}

interface CustomerGroup {
  scopeName?: string;
  rows: NotificationRow[];
}

// One notification covers a whole run and carries rows for every customer it touched, so an
// unrestricted reader gets patients from all of them in one list. Grouping restores the separation
// that having a notification per customer used to give for free (IP-512).
//
// Groups keep the order the producer sent them in, except that rows belonging to no customer are
// moved to the end: those are notes about the run - the overflow line, the export summary, or a
// person whose customer could not be resolved - and leaving them where they fell would file them
// under whichever customer happened to precede them.
//
// The reader only ever receives rows they are entitled to, so the headers are scope-filtered by
// construction: a customer with nothing visible here produces no group and therefore no header.
export const groupRowsByCustomer = (rows: NotificationRow[]): CustomerGroup[] => {
  const groups: CustomerGroup[] = [];

  rows.forEach((row) => {
    const scopeName = row.scopeName || undefined;
    const existing = groups.find((group) => group.scopeName === scopeName);

    if (existing) {
      existing.rows.push(row);
    } else {
      groups.push({ scopeName, rows: [row] });
    }
  });

  return [
    ...groups.filter((group) => group.scopeName !== undefined),
    ...groups.filter((group) => group.scopeName === undefined),
  ];
};

// Resolves the outcome phrase only. The person and personal id are separate values, placed into
// their own columns below, so this side owns the arrangement rather than inheriting it from the
// language string.
//
// Rendered as text, never through html-react-parser: these values are person names and free-text
// error messages arriving unescaped from the source system, so parsing them as markup would turn a
// patient name into an injection point.
const localizeRow = (row: NotificationRow) => {
  const resolved = Mashie.Resources.GetString(row.textKey);

  if (resolved.includes("Missing string")) {
    return row.textKey;
  }

  return format(resolved, row.values ?? {});
};

// The aggregate control keeps Notification_IsCompleted. This one answers a different question -
// "this line" rather than "everything I can see" - which is the distinction the per-row checkbox
// exists to draw, so sharing one caption would hide it in the surface a user would look at.
//
// Falls back to the aggregate's caption until Notification_Row_IsCompleted exists in Lokalise:
// GetString renders the literal "Missing string ..." for an unknown key, and a tooltip saying that
// is worse than one that is merely broad. Adding the key needs no code change here.
const rowCompletionCaption = () => {
  const resolved = Mashie.Resources.GetString("Notification_Row_IsCompleted");

  return resolved.includes("Missing string")
    ? Mashie.Resources.GetString("Notification_IsCompleted")
    : resolved;
};

// No checkbox at all on a row that asks for nothing. The absence is the signal: a disabled or
// greyed-out box still reads as an item to work through, which is the confusion this replaces - the
// aggregate checkbox appears whenever anything in the whole run needed attention, telling a reader
// nothing about which of their own lines it meant.
const renderRowCompletion = (notificationId: string, row: NotificationRow) => {
  const position = row.position;

  if (!row.requiresAction || position === undefined) {
    return <td className="notification-row-done" />;
  }

  return (
    <td className="notification-row-done">
      <input
        title={rowCompletionCaption()}
        type="checkbox"
        checked={!!row.isCompleted}
        onChange={() => NotificationStore.setRowDone(notificationId, position, !row.isCompleted)}
      />
    </td>
  );
};

const renderRow = (notificationId: string, row: NotificationRow, key: number) => {
  const values = row.values ?? {};

  // A row with no person - the overflow row, and the export summary rows - has nothing to put in
  // the identity columns, so it spans them. That is exactly what the pre-IP-504 table did with
  // colspan.
  if (!values.person) {
    return (
      <tr key={key}>
        {renderRowCompletion(notificationId, row)}
        <td colSpan={3}>{localizeRow(row)}</td>
      </tr>
    );
  }

  return (
    <tr key={key}>
      {renderRowCompletion(notificationId, row)}
      <td>{values.person}</td>
      <td>{values.personalId}</td>
      <td>{localizeRow(row)}</td>
    </tr>
  );
};

/**
 * The row table for one notification.
 *
 * Deliberately a function to CALL from the owning component's render, not a `<NotificationRows/>`
 * child. The rows are observable objects from the store, and MobX re-renders whichever observer
 * dereferenced them during its own render pass — so reading `row.isCompleted` here keeps that read
 * inside the parent `Notification`'s pass and its `@observer` continues to cover it. Turning this
 * into a child component would move the read out of the parent's pass, and unless the child were
 * itself an `@observer` a tick would write to the store and never repaint. That failure would be
 * invisible to the type checker and to the webpack build.
 */
export const renderNotificationRows = (notificationId: string, rows?: NotificationRow[]) => {
  if (!rows || rows.length === 0) {
    return null;
  }

  // A tbody per customer, so the header's scope="rowgroup" is true rather than decorative: each
  // group genuinely is the row group its header introduces. Assistive technology otherwise
  // announces the customer name as an ordinary data cell, which loses the grouping this adds.
  return (
    <table className="notification-rows">
      {groupRowsByCustomer(rows).map((group, groupIndex) => (
        <tbody key={groupIndex}>
          {group.scopeName ? (
            <tr className="notification-rows-customer">
              <th colSpan={4} scope="rowgroup">
                {group.scopeName}
              </th>
            </tr>
          ) : null}
          {group.rows.map((row, rowIndex) => renderRow(notificationId, row, rowIndex))}
        </tbody>
      ))}
    </table>
  );
};
